import { STRIPE_PAYMENT_PROVIDER_TYPE_ID } from '../../models/providers/stripe/stripe-provider-type';

// Realtime is a hint to fetch authoritative saved data, never an instruction to mutate it.
export const realtimeTypes = {
  classRegisterChanged: 'b781eb67-dac8-46c8-a218-6eb8d5af677e',
  attendanceExpectationUpdated: '5220076e-5710-4549-b616-0872ef879a90',
  newRegistration: '9dc1393f-770f-41d1-9525-992844fd6967',
  registrationUpdated: 'c1a7cb0c-8c89-4d1d-a639-9a6a2c954c51',
  termEmailInvitationChanged: '3cd5bc69-84b8-4af2-8241-cb00f3138a53',
  paymentStatusUpdated: 'ddeafd29-8352-44e2-831d-92e02c7a2053',
  paymentProviderConfigUpdated: '3810719b-fbf9-432c-a132-b7a4b7ee3f41',
  paymentProviderExternalAccountSetup: '140926c8-db43-4b95-93c1-415f60c2f6ca',
  clubUserActionsChanged: 'f2b0135c-4542-4547-b339-8eaf4f759f13',
  householdUserActionsChanged: 'd1374762-a638-481c-9e72-6ec5be48702f'
} as const;

export interface RealtimeEnvelope {
  type: string;
  payload: unknown;
}

export interface RegisterInvalidation {
  clubId: string;
  sessionId: string;
  registerId?: string;
}

export interface TermInvalidation {
  clubId: string;
  termId: string;
}

export interface PaymentInvalidation {
  invoiceId: string;
  clubId?: string;
}

const guidPattern = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

export function readRealtimeEnvelope(message: unknown): RealtimeEnvelope | undefined {
  if (typeof message !== 'string' || message.length > 65536) return undefined;

  try {
    const parsed: unknown = JSON.parse(message);
    if (!isObject(parsed) || typeof parsed['type'] !== 'string' || !guidPattern.test(parsed['type']) ||
        !('payload' in parsed) || !isObject(parsed['payload'])) return undefined;

    return { type: parsed['type'].toLowerCase(), payload: parsed['payload'] };
  } catch {
    return undefined;
  }
}

export function registerInvalidation(envelope: RealtimeEnvelope): RegisterInvalidation | undefined {
  if (envelope.type !== realtimeTypes.classRegisterChanged && envelope.type !== realtimeTypes.attendanceExpectationUpdated)
    return undefined;

  if (!isObject(envelope.payload)) return undefined;

  const clubId = envelope.payload['clubId'];
  const sessionId = envelope.payload['classSessionId'];
  const registerId = envelope.payload['classAttendanceId'];

  if (typeof clubId !== 'string' || !guidPattern.test(clubId) || typeof sessionId !== 'string' || !guidPattern.test(sessionId))
    return undefined;

  if (registerId != null && (typeof registerId !== 'string' || !guidPattern.test(registerId))) return undefined;
  if (envelope.type === realtimeTypes.classRegisterChanged && !registerId) return undefined;

  return {
    clubId: clubId.toLowerCase(),
    sessionId: sessionId.toLowerCase(),
    registerId: typeof registerId === 'string' ? registerId.toLowerCase() : undefined
  };
}

// Registration changes and term email invitation changes share the same narrow club/term hint.
export function termRegistrationInvalidation(envelope: RealtimeEnvelope): TermInvalidation | undefined {
  if (envelope.type !== realtimeTypes.newRegistration && envelope.type !== realtimeTypes.registrationUpdated &&
      envelope.type !== realtimeTypes.termEmailInvitationChanged) return undefined;

  if (!isObject(envelope.payload)) return undefined;

  const clubId = readGuid(envelope.payload['clubId']);
  const termId = readGuid(envelope.payload['termId']);
  return clubId && termId ? { clubId, termId } : undefined;
}

export function paymentInvalidation(envelope: RealtimeEnvelope): PaymentInvalidation | undefined {
  if (envelope.type !== realtimeTypes.paymentStatusUpdated || !isObject(envelope.payload)) return undefined;

  const invoiceId = readGuid(envelope.payload['invoiceId']);
  const clubValue = envelope.payload['clubId'];
  const clubId = clubValue == null ? undefined : readGuid(clubValue);

  if (!invoiceId || (clubValue != null && !clubId)) return undefined;
  return { invoiceId, clubId };
}

export interface PaymentProviderInvalidation {
  clubId: string;
  accountId?: string;
}

// These notifications are hints only: GET remains the authority for readiness and enabled state.
export function paymentProviderInvalidation(envelope: RealtimeEnvelope): PaymentProviderInvalidation | undefined {
  if (envelope.type !== realtimeTypes.paymentProviderConfigUpdated &&
      envelope.type !== realtimeTypes.paymentProviderExternalAccountSetup) return undefined;
  if (!isObject(envelope.payload)) return undefined;

  const clubId = readGuid(envelope.payload['clubId']);
  const isProgress = envelope.type === realtimeTypes.paymentProviderExternalAccountSetup;
  const accountId = isProgress ? readGuid(envelope.payload['accountId']) : undefined;
  if (!clubId || (isProgress && !accountId)) return undefined;

  const providerTypeId = readGuid(isProgress ? envelope.payload['providerTypeId'] : envelope.payload['paymentProviderTypeId']);
  if (providerTypeId !== STRIPE_PAYMENT_PROVIDER_TYPE_ID) return undefined;

  return { clubId, accountId };
}

export function clubUserActionsInvalidation(envelope: RealtimeEnvelope): string | undefined {
  return envelope.type === realtimeTypes.clubUserActionsChanged && isObject(envelope.payload)
    ? readGuid(envelope.payload['clubId']) : undefined;
}

export function householdUserActionsInvalidation(envelope: RealtimeEnvelope): string | undefined {
  return envelope.type === realtimeTypes.householdUserActionsChanged && isObject(envelope.payload)
    ? readGuid(envelope.payload['householdId']) : undefined;
}

function readGuid(value: unknown): string | undefined {
  return typeof value === 'string' && guidPattern.test(value) &&
    value.toLowerCase() !== '00000000-0000-0000-0000-000000000000' ? value.toLowerCase() : undefined;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
